import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Drawer } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getProductRotation } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeActionButton,
  NdoeResponsiveTable, NdoeDataCard, NdoeStatusBadge, NdoeImage,
  NdoeStats, NdoeStatCard, NdoeError,
  NdoeArticleLink, useFicheArticle,
} from '../../components/ndoe';
import { toCsv, downloadCsv } from './exportCsv';
import useRefLists from './useRefLists';
import ReportFilters from './ReportFilters';
import { money, days, rate, dateTxt, CONFIANCE_COURT } from './format';
import './rapports.css';

const rotationBadge = (row) => (
  <NdoeStatusBadge kind="custom" tone={row.rotation_tone} label={row.rotation_label} />
);
const confianceBadge = (row) => (
  <NdoeStatusBadge kind="custom" tone={row.confiance_tone} label={CONFIANCE_COURT[row.confiance] || row.confiance_label} />
);

const SORTS = [
  { value: 'score', label: 'Score de rotation' },
  { value: 'couverture', label: 'Urgence (stock le plus court)' },
  { value: 'cadence', label: 'Vitesse de vente' },
  { value: 'fifo', label: 'Délai FIFO le plus court' },
  { value: 'quantite', label: 'Quantité vendue' },
];
const ALERTES = [
  { value: '', label: 'Toutes les alertes' },
  { value: 'imminente', label: 'Rupture imminente (≤ 14 j)' },
  { value: 'proche', label: 'À réassortir bientôt (≤ 30 j)' },
];

const EMPTY_FILTERS = { marque: '', categorie: '', matiere: '', couleur: '', pointure: '', alerte: '' };

export default function RotationStock() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const [period, setPeriod] = useState({ period: 30 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('score');
  const [page, setPage] = useState(1);

  const [payload, setPayload] = useState({ meta: {}, summary: {}, data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const { openFiche, ficheElement } = useFicheArticle();

  const refs = useRefLists(isAdmin);

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [q]);

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
    ...filters, q: search, sort,
  }), [period, filters, search, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getProductRotation({ ...apiParams, page, page_size: 20 });
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger le rapport de rotation.');
    } finally { setLoading(false); }
  }, [apiParams, page]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  useEffect(() => { setPage(1); }, [period, filters, sort]);

  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value ?? '' }));
  const activeCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  const handleExport = async () => {
    setExporting(true);
    try {
      const all = [];
      let current = 1;
      let total = Infinity;
      while (all.length < total && current <= 25) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await getProductRotation({ ...apiParams, page: current, page_size: 60 });
        total = data.meta?.total ?? all.length;
        all.push(...(data.data || []));
        if (!data.data?.length) break;
        current += 1;
      }
      const columns = [
        { title: 'Produit', value: (r) => r.nom_produit },
        { title: 'Marque', value: (r) => r.marque },
        { title: 'Catégorie', value: (r) => r.categorie },
        { title: 'Matière', value: (r) => r.matiere },
        { title: 'Pointures moteur', value: (r) => r.pointures_moteur.map((t) => `P${t}`).join(' ') },
        { title: 'Quantité vendue (période)', value: (r) => r.quantite_vendue },
        { title: 'Vendu sur 30 derniers jours', value: (r) => r.quantite_recente },
        { title: "Chiffre d'affaires", value: (r) => r.chiffre_affaires },
        { title: 'Stock actuel', value: (r) => r.stock_actuel },
        { title: 'Couverture (jours restants)', value: (r) => r.couverture_jours ?? '' },
        { title: 'Âge du stock restant (j)', value: (r) => r.jours_en_stock ?? '' },
        { title: 'Délai FIFO moyen (j)', value: (r) => r.delai_fifo_jours ?? '' },
        { title: 'Dernière entrée', value: (r) => dateTxt(r.derniere_entree) },
        { title: 'Score de rotation', value: (r) => r.score_rotation },
        { title: 'Rotation', value: (r) => r.rotation_label },
        { title: 'Alerte', value: (r) => r.alerte_label || '' },
        { title: 'Confiance donnée', value: (r) => r.confiance_label },
      ];
      const { start_date: from, end_date: to } = payload.meta || {};
      downloadCsv(`rotation-stock_${from || ''}_${to || ''}.csv`, toCsv(columns, all));
      toast.success(`${all.length} produit(s) exporté(s).`);
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  const { summary = {}, meta = {}, data = [] } = payload;

  const columns = [
    {
      title: 'Photo', dataIndex: 'img', width: 76,
      render: (img, r) => <NdoeImage src={img} alt={r.nom_produit} size="md" preview={false} />,
    },
    {
      title: 'Produit', dataIndex: 'nom_produit',
      render: (nom, r) => (
        <div className="pp-produit">
          <b>{nom || '—'}</b>
          <small>{[r.marque, r.categorie, r.matiere].filter(Boolean).join(' · ') || '—'}</small>
          {r.pointures_moteur.length > 0 && (
            <div className="pp-pointures">
              {r.pointures_moteur.map((t) => <span className="pp-pt pp-pt--hot" key={t}>P{t}</span>)}
            </div>
          )}
          {r.deconseille && <NdoeStatusBadge kind="custom" tone="danger" label="Déconseillé" />}
        </div>
      ),
    },
    { title: 'Vendu', dataIndex: 'quantite_vendue', align: 'right', width: 82, render: (v) => <span className="num">{v}</span> },
    {
      title: 'Rythme actuel', dataIndex: 'cadence_recente', align: 'right', width: 120,
      render: (v) => <span className="num">{rate(v)}</span>,
    },
    { title: 'Stock', dataIndex: 'stock_actuel', align: 'right', width: 80, render: (v) => <span className="num">{v}</span> },
    {
      title: 'Tient encore', dataIndex: 'couverture_jours', align: 'right', width: 128,
      render: (v, r) => (
        <div className="rp-rupture">
          <b className={`num ${r.alerte === 'imminente' ? 'rot-urgent' : ''}`}>{days(v)}</b>
          {r.alerte_label && <small>{r.alerte_label}</small>}
        </div>
      ),
    },
    { title: 'Âge du stock', dataIndex: 'jours_en_stock', align: 'right', width: 110, render: (v) => <span className="num">{days(v)}</span> },
    { title: 'Délai FIFO', dataIndex: 'delai_fifo_jours', align: 'right', width: 100, render: (v) => <span className="num">{days(v)}</span> },
    {
      title: 'Rotation', dataIndex: 'score_rotation', width: 168,
      render: (v, r) => (
        <div className="rot-score">
          {rotationBadge(r)}
          <div className="rot-bar" aria-hidden="true"><i style={{ width: `${v}%` }} /></div>
          <small className="num">{v}/100</small>
        </div>
      ),
    },
    { title: 'Confiance', dataIndex: 'confiance', width: 126, render: (_, r) => confianceBadge(r) },
  ];

  const renderCard = (r) => (
    <NdoeDataCard
      key={r.id_produit}
      media={<NdoeImage src={r.img} alt={r.nom_produit} size="lg" preview={false} />}
      title={r.nom_produit || '—'}
      subtitle={[r.marque, r.categorie].filter(Boolean).join(' · ')}
      badge={rotationBadge(r)}
      metrics={[
        { label: 'Rythme actuel', value: rate(r.cadence_recente) },
        { label: 'Stock', value: r.stock_actuel },
        { label: 'Tient encore', value: days(r.couverture_jours) },
        { label: 'Score', value: `${r.score_rotation}/100` },
      ]}
      onClick={() => setDetail(r)}
    />
  );

  const KpiCard = ({ label, value, gold, onClick }) => (
    onClick ? (
      <div className="pp-kpi-click" role="button" tabIndex={0} onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}>
        <NdoeStatCard label={label} value={value} gold={gold} />
      </div>
    ) : <NdoeStatCard label={label} value={value} gold={gold} />
  );

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Rotation stock"
        subtitle="Ce qui tourne vite, combien de temps le stock tient, et quand réassortir."
        actions={
          <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Exporter CSV
          </NdoeActionButton>
        }
      />

      <div className="pp-period">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        {meta.start_date && (
          <span className="pp-range">du {dateTxt(meta.start_date)} au {dateTxt(meta.end_date)}</span>
        )}
      </div>

      <NdoeStats className="pp-stats">
        <KpiCard label="Produits en rotation" value={summary.produits ?? 0} />
        <KpiCard label="Rotation très rapide" value={summary.rotation_rapide ?? 0} gold />
        <KpiCard label="Rupture imminente" value={summary.rupture_imminente ?? 0}
          onClick={() => setFilter('alerte')('imminente')} />
        <KpiCard label="À réassortir bientôt" value={summary.a_reassortir_bientot ?? 0}
          onClick={() => setFilter('alerte')('proche')} />
        <KpiCard label="Délai FIFO moyen" value={days(summary.delai_fifo_moyen)} />
        <KpiCard label="Âge moyen du stock" value={days(summary.jours_en_stock_moyen)} />
      </NdoeStats>

      <p className="pp-note rp-note">
        <NdoeStatusBadge kind="custom" tone="info" label="Lecture" />
        {' '}La période choisit <b>quels produits</b> sont affichés et décrit leur rotation passée. En revanche,
        « tient encore », l'alerte et le score reposent toujours sur le <b>rythme des 30 derniers jours</b> —
        « si le rythme continue » parle du rythme actuel, pas d'une moyenne diluée sur 6 mois.
      </p>

      <ReportFilters
        q={q} setQ={setQ}
        filters={filters} setFilter={setFilter}
        refs={refs}
        sort={sort} setSort={setSort} sorts={SORTS}
        activeCount={activeCount}
      >
        <div className="ndoe-filter-bar__field">
          <Select placeholder="Alerte" style={{ minWidth: 205 }} options={ALERTES}
            value={filters.alerte} onChange={setFilter('alerte')} />
        </div>
      </ReportFilters>

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : (
        <NdoeResponsiveTable
          className="pp-table"
          data={data}
          columns={columns}
          renderCard={renderCard}
          rowKey="id_produit"
          loading={loading}
          error={error}
          onRetry={load}
          scroll={{ x: 1260 }}
          onRow={(record) => ({ onClick: () => setDetail(record), style: { cursor: 'pointer' } })}
          pagination={{ current: meta.page || 1, pageSize: meta.page_size || 20, total: meta.total || 0, showSizeChanger: false }}
          onChange={(pagination) => setPage(pagination?.current || 1)}
          empty={{
            icon: '🔄',
            title: 'Aucun produit en rotation sur cette période',
            text: 'Un produit « en rotation » a encore du stock et au moins une vente. Élargis la période.',
          }}
        />
      )}

      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        width={560}
        title={detail ? <NdoeArticleLink onClick={() => openFiche(detail.id_produit)}>{detail.nom_produit}</NdoeArticleLink> : ('' || 'Détail rotation')}
        className="pp-drawer"
      >
        {detail && (
          <div className="pp-detail">
            <div className="pp-detail__head">
              <NdoeImage src={detail.img} alt={detail.nom_produit} size="hero" />
              <div className="pp-detail__meta">
                <b>{[detail.marque, detail.categorie].filter(Boolean).join(' · ') || '—'}</b>
                <small>{detail.matiere || 'Matière non renseignée'}</small>
                <small>{(detail.couleurs || []).join(', ') || 'Couleur non renseignée'}</small>
                <div className="pp-detail__badges">
                  {rotationBadge(detail)}
                  {detail.alerte_label && <NdoeStatusBadge kind="custom" tone={detail.alerte_tone} label={detail.alerte_label} />}
                  {confianceBadge(detail)}
                </div>
              </div>
            </div>

            <div className="rp-raison">
              <span className="rp-raison__label">Ce que ça dit</span>
              <p>
                {detail.delai_fifo_jours !== null
                  ? <>Ce produit se vend en moyenne <b>{days(detail.delai_fifo_jours)}</b> après réception.{' '}</>
                  : <>Le délai entre réception et vente n'est pas mesurable (aucune réception tracée).{' '}</>}
                Il reste <b>{detail.stock_actuel} paire(s)</b>
                {detail.couverture_jours !== null
                  ? <> ; au rythme actuel, cela peut tenir environ <b>{detail.couverture_jours} jours</b>.</>
                  : <> ; aucune vente sur les 30 derniers jours, la projection est impossible.</>}
                {detail.pointures_moteur.length > 0 && (
                  <> Rotation surtout tirée par les pointures <b>{detail.pointures_moteur.map((t) => `P${t}`).join(', ')}</b>.</>
                )}
              </p>
            </div>

            <NdoeStats>
              <NdoeStatCard label="Score de rotation" value={`${detail.score_rotation}/100`} gold />
              <NdoeStatCard label="Rythme actuel" value={rate(detail.cadence_recente)} />
              <NdoeStatCard label="Vendu sur la période" value={detail.quantite_vendue} />
              <NdoeStatCard label="Chiffre d'affaires" value={money(detail.chiffre_affaires)} />
              <NdoeStatCard label="Stock actuel" value={detail.stock_actuel} />
              <NdoeStatCard label="Âge du stock" value={days(detail.jours_en_stock)} />
            </NdoeStats>

            <h4 className="pp-detail__title">Rotation par pointure</h4>
            <div className="pp-detail__pt rp-pt">
              <div className="pp-detail__pt-row rp-pt-row pp-detail__pt-row--head">
                <span>Pointure</span><span>Vendu</span><span>Stock</span><span>Tient</span>
              </div>
              {detail.pointures.map((p) => (
                <div className="pp-detail__pt-row rp-pt-row" key={p.taille}>
                  <span>P{p.taille}{detail.pointures_moteur.includes(p.taille) && <b className="rot-hot"> ●</b>}</span>
                  <span className="num">{p.vendu_recent}</span>
                  <span className="num">{p.stock}</span>
                  <span className="num">{days(p.couverture_jours)}</span>
                </div>
              ))}
            </div>

            <h4 className="pp-detail__title">Rotation (FIFO entrée → vente)</h4>
            {detail.delai_fifo_jours === null ? (
              <p className="pp-detail__warn">
                Aucune réception tracée&nbsp;: le délai FIFO et l'âge du stock ne sont pas calculés. Le score de
                rotation, lui, ne dépend que des ventes et reste fiable.
              </p>
            ) : (
              <p>
                Délai moyen <b>{days(detail.delai_fifo_jours)}</b>
                {detail.delai_fifo_min !== null && <> · min {days(detail.delai_fifo_min)} · max {days(detail.delai_fifo_max)}</>}
              </p>
            )}

            <h4 className="pp-detail__title">Chronologie</h4>
            <ul className="pp-detail__dates">
              <li><span>Dernière entrée en stock</span><b>{dateTxt(detail.derniere_entree)}</b></li>
              <li><span>Âge du stock restant</span><b>{days(detail.jours_en_stock)}</b></li>
            </ul>
          </div>
        )}
      </Drawer>
      {ficheElement}
    </NdoePage>
  );
}
